import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { PROGRAMS } from "@/data/content";
import { scrollToId } from "@/hooks/useLenis";

export default function Programs() {
  return (
    <section
      id="programs"
      className="relative py-28 md:py-36"
      data-testid="programs-section"
    >
      <div className="section-x mx-auto max-w-[1400px]">
        <div className="max-w-3xl">
          <p className="section-eyebrow">04 — Training Programs</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl leading-[1.12] tracking-tight">
            <span className="block">Industry-focused training.</span>
            <span className="mt-1 block text-ink/50">
              Built from 12+ years of experience.
            </span>
          </h2>
        </div>

        {/* Bento-ish grid */}
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {PROGRAMS.map((p, i) => (
            <ProgramCard program={p} index={i} key={p.name} />
          ))}
        </div>

        {/* CTA banner */}
        <UpskillBanner />
      </div>
    </section>
  );
}

function ProgramCard({ program, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const featured = index === 0 || index === 9; // Selenium & Generative AI
  return (
    <motion.button
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: (index % 4) * 0.06 }}
      onClick={() => scrollToId("contact")}
      className={`group relative overflow-hidden text-left rounded-2xl border p-5 md:p-6 hover:-translate-y-1 transition-[transform,border-color,background-color] duration-300 ${
        featured
          ? "border-accent-cyan/25 bg-accent-cyan/[0.04] hover:border-accent-cyan/60"
          : "border-ink/[0.1] bg-ink/[0.02] hover:border-ink/25"
      }`}
      data-testid={`program-card-${index}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.28em] text-ink/55 font-mono">
          Program {String(index + 1).padStart(2, "0")}
        </span>
        {featured && (
          <span className="inline-flex items-center gap-1 rounded-full border border-accent-cyan/40 bg-accent-cyan/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-accent-cyan">
            <Sparkles size={10} /> Featured
          </span>
        )}
      </div>
      <h3 className="mt-4 font-display text-xl md:text-[22px] tracking-tight leading-tight">
        {program.name}
      </h3>
      <p className="mt-2 text-ink/55 text-[13.5px] leading-relaxed">
        {program.detail}
      </p>
      <div className="mt-5 inline-flex items-center gap-1 text-xs text-ink/50 group-hover:text-accent-cyan transition-colors">
        Enquire <ArrowUpRight size={13} />
      </div>
    </motion.button>
  );
}

function UpskillBanner() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7 }}
      className="mt-14 relative overflow-hidden rounded-3xl border border-ink/10 bg-gradient-to-br from-ink/[0.05] to-transparent p-8 md:p-12"
      data-testid="upskill-banner"
    >
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-accent-cyan/15 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-accent-purple/15 blur-3xl" />
      <div className="relative grid md:grid-cols-[1fr_auto] gap-8 items-end">
        <div>
          <p className="text-[11px] uppercase tracking-[0.32em] text-accent-cyan/80 font-mono">
            For engineering leaders
          </p>
          <h3 className="mt-3 font-display text-3xl md:text-5xl tracking-tight leading-[1.05] max-w-2xl">
            Looking to upskill <br className="hidden sm:block" />
            <span className="text-ink/50">your QA team?</span>
          </h3>
          <p className="mt-4 text-ink/60 max-w-xl">
            Custom, hands-on training programs designed around your team&apos;s
            stack, goals and timelines.
          </p>
        </div>
        <button
          onClick={() => scrollToId("contact")}
          className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 text-white px-6 py-3.5 text-sm font-semibold shadow-[0_8px_30px_rgba(139,92,246,0.35)] hover:shadow-[0_12px_40px_rgba(139,92,246,0.55)] transition-shadow"
          data-testid="upskill-cta"
        >
          Get in touch
          <ArrowUpRight
            size={16}
            className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </button>
      </div>
    </motion.div>
  );
}
